/**
 * OfertaDocument — рендер структурированного текста оферты.
 *
 * Принимает сырой plain-text, парсит в блоки через parseOferta,
 * и отрисовывает как editorial-типографику:
 *   - Hero-блок: monumental display-заголовок + subtitle + intro
 *   - Section: большой gold-номер в левой колонке + CAPS-заголовок +
 *     абзацы с мелкой нумерацией и glass-bullet-списками
 *   - Ack: финальный блок «я подтверждаю» с checkmark-буллетами
 *
 * Никакой логики состояния — это чистый презентационный компонент.
 */
import { parseOferta, type OfertaBlock, type Paragraph } from './parseOferta';

interface OfertaDocumentProps {
  content: string;
}

export function OfertaDocument({ content }: OfertaDocumentProps) {
  const blocks = parseOferta(content);

  return (
    <article className="mx-auto max-w-[680px] px-6 pb-[180px] pt-8 text-[var(--text-primary)]">
      {blocks.map((b, i) => {
        if (b.kind === 'hero') return <HeroBlock key={`hero-${i}`} block={b} />;
        if (b.kind === 'section') return <SectionBlock key={`s-${i}`} block={b} />;
        return <AckBlock key={`ack-${i}`} block={b} />;
      })}
    </article>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Hero
// ──────────────────────────────────────────────────────────────────────

function HeroBlock({
  block,
}: {
  block: Extract<OfertaBlock, { kind: 'hero' }>;
}) {
  return (
    <header className="mb-12 pt-4">
      {/* Eyebrow */}
      <div
        className="mb-6 inline-flex items-center gap-2 rounded-full border border-[rgba(245,217,126,0.25)] bg-[rgba(245,217,126,0.05)] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-[var(--color-lunar-500)]"
      >
        <span className="size-1.5 rounded-full bg-[var(--color-lunar-500)] shadow-[0_0_10px_var(--color-lunar-500)]" />
        {block.title}
      </div>

      {/* Display title */}
      <h1
        className="font-display text-[30px] leading-[1.08] tracking-[-0.01em] text-white sm:text-[35px]"
        style={{ fontWeight: 700 }}
      >
        {block.subtitle.split(' ').map((word, wi) => (
          <span key={wi} className="inline-block">
            {word}
            &nbsp;
          </span>
        ))}
      </h1>

      {/* Hairline divider */}
      <div
        className="my-8 h-px w-full"
        style={{
          background:
            'linear-gradient(90deg, transparent 0%, rgba(245,217,126,0.5) 50%, transparent 100%)',
        }}
      />

      {/* Intro paragraphs */}
      <div className="space-y-5 text-[15px] leading-[1.7] text-[rgba(255,255,255,0.82)]">
        {block.intro.map((para, pi) => (
          <p key={pi} className="font-serif">
            {para}
          </p>
        ))}
      </div>
    </header>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Section
// ──────────────────────────────────────────────────────────────────────

function SectionBlock({
  block,
}: {
  block: Extract<OfertaBlock, { kind: 'section' }>;
}) {
  return (
    <section className="mb-14">
      <div className="mb-6 flex items-baseline gap-5">
        <div
          className="font-display text-[56px] leading-[0.9] text-[var(--color-lunar-500)]"
          style={{
            fontWeight: 300,
            fontFeatureSettings: '"tnum"',
            textShadow: '0 2px 24px rgba(245,217,126,0.35)',
          }}
        >
          {block.num ? `${block.num.padStart(2, '0')}` : '—'}
        </div>
        <h2
          className="font-display text-[15px] font-semibold uppercase tracking-[0.24em] text-white"
          style={{ letterSpacing: '0.24em' }}
        >
          {block.title}
        </h2>
      </div>

      <div className="space-y-5 pl-[4px]">
        {block.paragraphs.map((p, pi) => (
          <ClauseBlock key={pi} paragraph={p} />
        ))}
      </div>
    </section>
  );
}

function ClauseBlock({ paragraph }: { paragraph: Paragraph }) {
  return (
    <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-2 text-[14.5px] leading-[1.72] text-[rgba(255,255,255,0.82)]">
      <div
        className="select-none pt-[2px] font-mono text-[12px] tracking-tight text-[rgba(255,255,255,0.45)]"
        style={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {paragraph.num ?? ''}
      </div>
      <div className="space-y-3">
        {paragraph.text && <p className="font-serif">{paragraph.text}</p>}
        {paragraph.bullets.length > 0 && (
          <ul className="space-y-2 pl-1">
            {paragraph.bullets.map((b, bi) => (
              <li
                key={bi}
                className="flex gap-3 font-serif text-[14px] leading-[1.65] text-[rgba(255,255,255,0.78)]"
              >
                <span
                  className="mt-[10px] size-1 shrink-0 rounded-full bg-[rgba(245,217,126,0.55)]"
                  aria-hidden
                />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Ack
// ──────────────────────────────────────────────────────────────────────

function AckBlock({
  block,
}: {
  block: Extract<OfertaBlock, { kind: 'ack' }>;
}) {
  return (
    <section
      className="relative mt-6 overflow-hidden rounded-[28px] border border-[rgba(245,217,126,0.22)] p-8"
      style={{
        background:
          'linear-gradient(180deg, rgba(245,217,126,0.06) 0%, rgba(245,217,126,0.015) 100%)',
        boxShadow:
          '0 18px 48px -18px rgba(245,217,126,0.2), inset 0 1px 0 rgba(245,217,126,0.12)',
      }}
    >
      <div
        className="pointer-events-none absolute -left-10 -top-10 size-40 rounded-full opacity-60"
        style={{
          background:
            'radial-gradient(50% 50% at 50% 50%, rgba(245,217,126,0.25) 0%, transparent 70%)',
          filter: 'blur(20px)',
        }}
      />
      <h2
        className="mb-6 font-display text-[13px] font-semibold uppercase tracking-[0.26em] text-[var(--color-lunar-500)]"
      >
        {block.title}
      </h2>
      <ul className="space-y-4">
        {block.bullets.map((b, i) => (
          <li
            key={i}
            className="flex gap-4 font-serif text-[14.5px] leading-[1.65] text-[rgba(255,255,255,0.92)]"
          >
            <span
              className="mt-[2px] inline-flex size-5 shrink-0 items-center justify-center rounded-full border border-[rgba(245,217,126,0.4)] bg-[rgba(245,217,126,0.08)]"
              aria-hidden
            >
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                <path
                  d="M1.5 5.2 L4 7.5 L8.5 2.5"
                  stroke="var(--color-lunar-500)"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            <span>{b}</span>
          </li>
        ))}
      </ul>
      {block.signature && (
        <div className="mt-8 border-t border-[rgba(255,255,255,0.08)] pt-5 font-serif text-[12.5px] leading-[1.6] text-[rgba(255,255,255,0.55)]">
          {block.signature.split('\n').map((l, li) => (
            <div key={li}>{l}</div>
          ))}
        </div>
      )}
    </section>
  );
}
